<template>
  <div class="warehouse-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.Warehouse.line6_text') }}</h1>
      <div class="header-actions">
        <button class="btn-add" @click="showAddDialog = true">{{ $t('wholesaler.Warehouse.line8_text') }}</button>
      </div>
    </header>

    <!-- 仓库列表 -->
    <div class="warehouse-list">
      <!-- 店铺仓库（默认仓库） -->
      <div class="warehouse-card default-warehouse" v-if="defaultWarehouse">
        <div class="warehouse-header">
          <span class="warehouse-icon">🏪</span>
          <div class="warehouse-info">
            <h3>{{ defaultWarehouse.warehouseName }}</h3>
            <p class="warehouse-type">{{ $t('wholesaler.Warehouse.line20_text') }}</p>
          </div>
          <span class="readonly-badge">{{ $t('wholesaler.Warehouse.line22_text') }}</span>
        </div>
        <div class="warehouse-details">
          <div class="detail-item">
            <span class="label">{{ $t('wholesaler.Warehouse.line26_text') }}</span>
            <span class="value">{{ defaultWarehouse.warehouseAddress || $t('wholesaler.Warehouse.notSet') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">{{ $t('wholesaler.Warehouse.line30_text') }}</span>
            <span class="value">{{ defaultWarehouse.warehouseLocation || $t('wholesaler.Warehouse.notSet') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">{{ $t('wholesaler.Warehouse.line34_text') }}</span>
            <span class="value">{{ defaultWarehouse.warehousePhone || $t('wholesaler.Warehouse.notSet') }}</span>
          </div>
        </div>
      </div>

      <!-- 分仓库列表 -->
      <div class="warehouse-card" v-for="warehouse in additionalWarehouses" :key="warehouse.id">
        <div class="warehouse-header">
          <span class="warehouse-icon">📦</span>
          <div class="warehouse-info">
            <h3>{{ warehouse.warehouseName }}</h3>
            <p class="warehouse-type">{{ $t('wholesaler.Warehouse.line46_text') }}</p>
          </div>
          <div class="warehouse-actions">
            <button class="btn-edit" @click="editWarehouse(warehouse)">{{ $t('common.edit') }}</button>
            <button class="btn-delete" @click="deleteWarehouse(warehouse)">{{ $t('common.delete') }}</button>
          </div>
        </div>
        <div class="warehouse-details">
          <div class="detail-item">
            <span class="label">{{ $t('wholesaler.Warehouse.line55_text') }}</span>
            <span class="value">{{ warehouse.warehouseAddress || $t('wholesaler.Warehouse.notSet') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">{{ $t('wholesaler.Warehouse.line59_text') }}</span>
            <span class="value">{{ warehouse.warehouseLocation || $t('wholesaler.Warehouse.notSet') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">{{ $t('wholesaler.Warehouse.line63_text') }}</span>
            <span class="value">{{ warehouse.warehousePhone || $t('wholesaler.Warehouse.notSet') }}</span>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div class="empty-state" v-if="additionalWarehouses.length === 0 && !defaultWarehouse">
        <p>{{ $t('wholesaler.Warehouse.line71_text') }}</p>
      </div>
    </div>

    <!-- 添加/编辑仓库对话框 -->
    <div class="dialog-overlay" v-if="showAddDialog || showEditDialog" @click="closeDialog">
      <div class="dialog-content" @click.stop>
        <div class="dialog-header">
          <h3>{{ showAddDialog ? $t('wholesaler.Warehouse.addWarehouse') : $t('wholesaler.Warehouse.editWarehouse') }}</h3>
          <button class="btn-close" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
          <div class="form-item">
            <label>{{ $t('wholesaler.Warehouse.line84_text') }}</label>
            <input 
              type="text" 
              v-model="formData.warehouseName"
              :placeholder="$t('wholesaler.Warehouse.line88_placeholder')"
            />
          </div>
          <div class="form-item">
            <label>{{ $t('wholesaler.Warehouse.line92_text') }}</label>
            <div class="address-input-row">
              <input 
                type="text" 
                v-model="formData.warehouseAddress"
                :placeholder="$t('wholesaler.Warehouse.line97_placeholder')"
              />
              <button type="button" class="btn-map-pick" @click="showMapPicker = true">
                📍 {{ $t('wholesaler.Warehouse.mapPick') }}
              </button>
            </div>
          </div>
          <div class="form-item">
            <label>{{ $t('wholesaler.Warehouse.line105_text') }}</label>
            <input 
              type="text" 
              v-model="formData.warehouseLocation"
              :placeholder="$t('wholesaler.Warehouse.line109_placeholder')"
            />
          </div>
          <div class="form-item">
            <label>{{ $t('wholesaler.Warehouse.line113_text') }}</label>
            <input 
              type="text" 
              v-model="formData.warehousePhone"
              :placeholder="$t('wholesaler.Warehouse.line117_placeholder')"
            />
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn-cancel" @click="closeDialog">{{ $t('common.cancel') }}</button>
          <button class="btn-save" @click="saveWarehouse">{{ $t('common.save') }}</button>
        </div>
      </div>
    </div>

    <!-- Toast 提示 -->
    <div class="toast" v-if="showToast">{{ toastMessage }}</div>

    <!-- 地图选点组件 -->
    <MapPicker
      v-if="showMapPicker"
      @confirm="onMapConfirmed"
      @cancel="showMapPicker = false"
    />
  </div>
</template>

<script>
import api from '@/services/api';
import { getWholesalerId } from '@/utils/wholesaler';
import MapPicker from '@/components/MapPicker.vue';

export default {
  name: 'WarehouseManagement',
  components: { MapPicker },
  
  data() {
    return {
      warehouses: [],
      defaultWarehouse: null,
      additionalWarehouses: [],
      showAddDialog: false,
      showEditDialog: false,
      editingWarehouseId: null,
      formData: {
        warehouseName: '',
        warehouseAddress: '',
        warehouseLocation: '',
        longitude: '',
        latitude: '',
        warehousePhone: ''
      },
      showMapPicker: false,
      showToast: false,
      toastMessage: ''
    };
  },
  
  mounted() {
    this.loadWarehouses();
  },
  
  methods: {
    async loadWarehouses() {
      try {
        const wholesalerId = getWholesalerId() ?? 1;
        const response = await api.get('/warehouse/list', { params: { wholesalerId } });
        
        // ⭐ api.js 响应拦截器已经解包，response 直接是数组
        this.warehouses = Array.isArray(response) ? response : [];
        
        // 分离默认仓库和分仓库
        this.defaultWarehouse = this.warehouses.find(w => w.isDefault) || null;
        this.additionalWarehouses = this.warehouses.filter(w => !w.isDefault) || [];
        
        console.log('✅ 仓库列表加载成功:', this.warehouses);
        console.log('📦 默认仓库:', this.defaultWarehouse);
        console.log('📦 分仓库:', this.additionalWarehouses);
      } catch (error) {
        console.error('❌ 加载仓库列表失败:', error);
        this.showToastMsg(this.$t('wholesaler.Warehouse.loadFailed') + '：' + (error.response?.data?.message || error.message));
      }
    },
    

    
    editWarehouse(warehouse) {
      this.showEditDialog = true;
      this.editingWarehouseId = warehouse.id;
      this.formData = {
        warehouseName: warehouse.warehouseName || '',
        warehouseAddress: warehouse.warehouseAddress || '',
        warehouseLocation: warehouse.warehouseLocation || '',
        longitude: warehouse.longitude || '',
        latitude: warehouse.latitude || '',
        warehousePhone: warehouse.warehousePhone || ''
      };
    },
    
    async deleteWarehouse(warehouse) {
      if (!confirm(this.$t('wholesaler.Warehouse.confirmDeleteMsg', { name: warehouse.warehouseName }))) {
        return;
      }
      
      try {
        await api.delete(`/warehouse/${warehouse.id}`);
        this.showToastMsg(this.$t('wholesaler.Warehouse.deleteSuccess'));
        this.loadWarehouses();
      } catch (error) {
        console.error('❌ 删除仓库失败:', error);
        this.showToastMsg(error.response?.data?.message || this.$t('wholesaler.Warehouse.deleteFailed'));
      }
    },
    
    async saveWarehouse() {
      if (!this.formData.warehouseName?.trim()) {
        this.showToastMsg(this.$t('wholesaler.Warehouse.pleaseInputName'));
        return;
      }
      
      try {
        const wholesalerId = getWholesalerId() ?? 1;
        
        if (this.showAddDialog) {
          // 新建仓库
          await api.post('/warehouse', {
            ...this.formData,
            wholesalerId: wholesalerId,
            isDefault: false
          });
          this.showToastMsg(this.$t('wholesaler.Warehouse.createSuccess'));
        } else {
          // 更新仓库
          await api.put(`/warehouse/${this.editingWarehouseId}`, this.formData);
          this.showToastMsg(this.$t('wholesaler.Warehouse.updateSuccess'));
        }
        
        this.closeDialog();
        this.loadWarehouses();
      } catch (error) {
        console.error('❌ 保存仓库失败:', error);
        this.showToastMsg(error.response?.data?.message || this.$t('wholesaler.Warehouse.saveFailed'));
      }
    },
    
    closeDialog() {
      this.showAddDialog = false;
      this.showEditDialog = false;
      this.editingWarehouseId = null;
      this.formData = {
        warehouseName: '',
        warehouseAddress: '',
        warehouseLocation: '',
        longitude: '',
        latitude: '',
        warehousePhone: ''
      };
    },
    onMapConfirmed(location) {
      this.formData.warehouseAddress = location.address;
      this.formData.latitude = String(location.lat);
      this.formData.longitude = String(location.lng);
      this.formData.warehouseLocation = `${location.lat.toFixed(6)},${location.lng.toFixed(6)}`;
      this.showMapPicker = false;
    },
    
    showToastMsg(message) {
      this.toastMessage = message;
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 2000);
    },
    
    goBack() {
      this.$router.push('/my-profile');
    }
  }
};
</script>

<style scoped>
.warehouse-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.back-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(16px + env(safe-area-inset-top, 0px)) 20px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.back-btn {
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.back-header h1 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-add {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.warehouse-list {
  padding: 20px;
}

.warehouse-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.default-warehouse {
  border: 2px solid #667eea;
}

.warehouse-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.warehouse-icon {
  font-size: 32px;
}

.warehouse-info {
  flex: 1;
}

.warehouse-info h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  color: #111827;
}

.warehouse-type {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.warehouse-actions {
  display: flex;
  gap: 8px;
}

.btn-edit {
  padding: 6px 12px;
  background: #f3f4f6;
  color: #667eea;
  border: 1px solid #667eea;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

.btn-delete {
  padding: 6px 12px;
  background: #fff;
  color: #ef4444;
  border: 1px solid #ef4444;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

.readonly-badge {
  padding: 4px 12px;
  background: #f3f4f6;
  color: #999;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #e5e7eb;
}

.warehouse-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: #666;
}

.detail-item .label {
  white-space: nowrap;
  color: #999;
}

.address-input-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.address-input-row input {
  flex: 1;
}

.btn-map-pick {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-map-pick:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-map-pick:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

/* 对话框样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.dialog-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.dialog-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-header h3 {
  margin: 0;
  font-size: 16px;
  color: #111827;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}

.dialog-body {
  padding: 20px;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
}

.form-item input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-item input:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

.dialog-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.btn-cancel,
.btn-save {
  flex: 1;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}

.btn-cancel {
  background: #f3f4f6;
  color: #666;
  border: none;
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
}

/* Toast 样式 */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10000;
}
</style>
